// The compliance calendar.
//
// This is the screen the module exists for. Nearly every landlord-tenant liability
// in Illinois is a missed date, and the penalties are not proportionate to the
// oversight — twice the deposit for a late itemisation, the entire deposit for
// unpaid Chicago interest, a void notice for Fair Notice served a day short.
//
// So each row carries three things the manager needs at once: when it is due, what
// happens if it is not met, and which layer of law produced it. Marking one met
// requires evidence, because a deadline recorded as done with nothing behind it
// looks compliant in the system and proves nothing in a courtroom.

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Stat, Pill, Table, Modal, Textarea, Button,
    Notice, Loading, Empty, fmtDate, titleCase,
} from '../../components/secure/pm/PMKit';

export default function PMDeadlinesPage() {
    const apiClient = useApiClient();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ overdue: [], due_soon: [], upcoming: [] });
    const [status, setStatus] = useState('open');
    const [within, setWithin] = useState(120);
    const [modal, setModal] = useState(null);
    const [evidence, setEvidence] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await apiClient.get(
                `/api/property-management/deadlines?status=${status}&within_days=${within}`));
        } catch (e) { setError({ message: e.message }); } finally { setLoading(false); }
    }, [apiClient, status, within]);

    useEffect(() => { load(); }, [load]);

    const markMet = async () => {
        setBusy(true); setError(null);
        try {
            await apiClient.post(`/api/property-management/deadlines/${modal.deadline_id}/met`, { evidence });
            setModal(null); setEvidence('');
            await load();
        } catch (e) { setError({ message: e.message }); } finally { setBusy(false); }
    };

    const columns = [
        { key: 'due_on', label: 'Due', nowrap: true,
            render: (r) => (
                <div>
                    <div className="font-medium text-slate-800">{fmtDate(r.due_on)}</div>
                    <div className={`text-xs ${r.days_out < 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>
                        {r.days_out < 0 ? `${Math.abs(r.days_out)} days overdue`
                            : r.days_out === 0 ? 'today' : `in ${r.days_out} days`}
                    </div>
                </div>
            ) },
        { key: 'label', label: 'Obligation',
            render: (r) => (
                <div className="max-w-xl">
                    <div className="text-slate-800">{r.label}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                        {r.property_name}{r.unit_number ? ` · unit ${r.unit_number}` : ''}
                    </div>
                    {r.consequence && (
                        <div className="text-xs text-rose-700/80 mt-1 italic">{r.consequence}</div>
                    )}
                </div>
            ) },
        { key: 'obligation', label: 'Type', nowrap: true,
            render: (r) => <Pill>{titleCase(r.obligation)}</Pill> },
        { key: 'jurisdiction', label: 'Authority', nowrap: true,
            render: (r) => (
                <div>
                    {r.jurisdiction ? <Pill tone="indigo">{r.jurisdiction}</Pill>
                        : <span className="text-slate-300 text-xs">—</span>}
                    {r.authority && <div className="text-[10px] text-slate-400 mt-1 max-w-[16rem]">{r.authority}</div>}
                </div>
            ) },
        { key: 'act', label: '', nowrap: true,
            render: (r) => r.status === 'open'
                ? <Button variant="ghost" onClick={() => { setModal(r); setEvidence(''); }}>Mark met</Button>
                : <Pill tone="good">{titleCase(r.status)}</Pill> },
    ];

    if (loading) return <Loading />;

    const all = [...data.overdue, ...data.due_soon, ...data.upcoming];

    return (
        <div>
            <PageHeader
                title="Compliance Calendar"
                subtitle="Every obligation carries the layer of law that produced it, so a computed date can always be explained."
            >
                <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="px-3 py-2 border border-slate-300 rounded text-sm bg-white">
                    {['open', 'met', 'missed', 'waived'].map((s) =>
                        <option key={s} value={s}>{titleCase(s)}</option>)}
                </select>
                <select value={within} onChange={(e) => setWithin(Number(e.target.value))}
                    className="px-3 py-2 border border-slate-300 rounded text-sm bg-white">
                    {[30, 90, 120, 365, 1825].map((d) =>
                        <option key={d} value={d}>{d >= 365 ? `${d / 365} year${d > 365 ? 's' : ''}` : `${d} days`}</option>)}
                </select>
            </PageHeader>

            {error && <Notice tone="bad" message={error.message} onDismiss={() => setError(null)} />}

            <div className="grid grid-cols-3 gap-3 mb-6">
                <Stat label="Overdue" value={data.overdue.length}
                    tone={data.overdue.length ? 'bad' : 'good'} sub="penalties already running" />
                <Stat label="Due within 14 days" value={data.due_soon.length}
                    tone={data.due_soon.length ? 'warn' : 'good'} />
                <Stat label="Upcoming" value={data.upcoming.length} />
            </div>

            {all.length === 0 ? (
                <Panel><Empty title="Nothing in this window"
                    message="Deadlines are generated when a lease is created and when a tenant moves out." /></Panel>
            ) : (
                <>
                    {data.overdue.length > 0 && (
                        <div className="mb-5">
                            <Panel title="Overdue" subtitle="Consequences are already accruing on these">
                                <Table columns={columns}
                                    rows={data.overdue.map((r) => ({ ...r, _key: r.deadline_id }))}
                                    rowTone={() => 'bad'} />
                            </Panel>
                        </div>
                    )}
                    {data.due_soon.length > 0 && (
                        <div className="mb-5">
                            <Panel title="Due within a fortnight">
                                <Table columns={columns}
                                    rows={data.due_soon.map((r) => ({ ...r, _key: r.deadline_id }))}
                                    rowTone={() => 'warn'} />
                            </Panel>
                        </div>
                    )}
                    {data.upcoming.length > 0 && (
                        <Panel title="Upcoming">
                            <Table columns={columns}
                                rows={data.upcoming.map((r) => ({ ...r, _key: r.deadline_id }))} />
                        </Panel>
                    )}
                </>
            )}

            {modal && (
                <Modal title="Record this obligation as met"
                    subtitle={modal.label}
                    onClose={() => setModal(null)}>
                    <p className="text-sm text-slate-600 mb-4">
                        Due {fmtDate(modal.due_on)}
                        {modal.authority && <> under <span className="font-medium">{modal.authority}</span></>}.
                    </p>
                    <Textarea
                        label="What was done, and how (required)"
                        value={evidence}
                        onChange={(e) => setEvidence(e.target.value)}
                        placeholder="e.g. Itemised statement and paid receipts posted certified mail 9114 0000 0000 0000, tracking retained in the lease file"
                        rows={4}
                    />
                    <p className="text-xs text-slate-500 -mt-1 mb-4">
                        A deadline marked met with nothing behind it looks compliant here and proves nothing later.
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy || !evidence.trim()} onClick={markMet}>
                            {busy ? 'Recording…' : 'Record as met'}</Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
